import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  accountService,
  sellerService,
  tileService,
} from "../../services/enhancedCrudService";
import { toast, ToastContainer } from "react-toastify";
import { Crop } from "../../shared/crop";
import { MAX_PRODUCT_IMAGE_SIZE } from "./../../shared/constant";
import Select from "react-select";

const AccountSignUp = () => {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  const [formData, setFormData] = useState({
    mobile_number: "",
    username: "",
    pin: "",
    role: "SELLER",
    firm_name: "",
    gst_number: "",
    owner_name: "",
    office_mobile: "",
    address_line: "",
    road: "",
    city: "",
    district: null,
    district_name_display: "",
    state: null,
    state_name_display: "",
    pin_code: "",
    category_names: [],
    seller_type: "",
    tile_type: "",
    tile_size: "",
    seller_place: "",
    verification_token: "",
  });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [otpCode, setOtpCode] = useState("");
  const [otpText, setOtpText] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [showResendOTP, setShowResendOTP] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [gstValidation, setGstValidation] = useState({
    isValid: false,
    message: "",
  });

  const SELLER_PLACE_CHOICES = [
    { value: "morbi", label: "Morbi" },
    { value: "than", label: "Than" },
    { value: "imported", label: "Imported" },
  ];

  const sellerTypes = [
    { value: "manufacturer", label: "Manufacturer" },
    { value: "importer", label: "Importer" },
    { value: "stock_premium", label: "Stockiest Only Premium" },
    { value: "stock_other", label: "Stockiest Other Grade" },
  ];

  const fetchTypes = async () => {
    try {
      const response = await tileService.getTypes();
      if (response.types && response.types.length) {
        let mapTyped = response.types.map((row) => {
          return {
            label: row.name,
            value: row.id,
          };
        });
        setTypes(mapTyped);
      }
    } catch (error) {
      console.error("Error fetching types:", error);
    }
  };

  const fetchSizes = async () => {
    try {
      const response = await tileService.getSizes();
      if (response.types && response.types.length) {
        let mapTyped = response.types.map((row) => {
          return {
            label: row.name,
            value: row.id,
          };
        });
        setSizes(mapTyped);
      }
    } catch (error) {
      console.error("Error fetching sizes:", error);
    }
  };

  const startCountdown = () => {
    setRemaining(120);
  };

  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const fetchCategories = async () => {
    const response = await sellerService.getCategoryList();
    if (response && response.length > 0) {
      let cMap = response.map((row) => {
        return {
          label: row.name,
          value: row.name,
        };
      });
      setCategories(cMap);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await accountService.getStates();
      console.log("States API response:", response); // Debug log
      if (response && response.states && response.states.length > 0) {
        const mappedStates = response.states.map((state) => ({
          label: state.name,
          value: state.id,
        }));
        console.log("Mapped states:", mappedStates); // Debug log
        setStates(mappedStates);
      } else {
        console.log(
          "No states found in response, trying alternative structure"
        );
        // Try alternative response structure
        if (response && Array.isArray(response)) {
          const mappedStates = response.map((state) => ({
            label: state.name,
            value: state.id,
          }));
          setStates(mappedStates);
        } else {
          setStates([]);
        }
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      console.log("States API failed - no fallback data provided");
      setStates([]);
    }
  };

  const fetchDistricts = async (stateId) => {
    try {
      if (!stateId) {
        setDistricts([]);
        return;
      }
      const response = await accountService.getDistricts(stateId);
      console.log("Districts API response:", response); // Debug log
      if (response && Array.isArray(response) && response.length > 0) {
        const mappedDistricts = response.map((district) => ({
          label: district.name,
          value: district.id,
        }));
        console.log("Mapped districts:", mappedDistricts); // Debug log
        setDistricts(mappedDistricts);
      } else if (
        response &&
        response.districts &&
        response.districts.length > 0
      ) {
        // Fallback for different response structure
        const mappedDistricts = response.districts.map((district) => ({
          label: district.name,
          value: district.id,
        }));
        console.log("Mapped districts (fallback):", mappedDistricts); // Debug log
        setDistricts(mappedDistricts);
      } else {
        setDistricts([]);
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
      setDistricts([]);
    }
  };

  const handleStateChange = (selectedState) => {
    const stateId = selectedState?.value;
    const stateName = selectedState?.label;

    setFormData((prev) => ({
      ...prev,
      state: stateId || null,
      state_name_display: stateName || "",
      district: null, // Reset district when state changes
      district_name_display: "",
    }));

    // Fetch districts for the selected state
    fetchDistricts(stateId);
  };

  const handleDistrictChange = (selectedDistrict) => {
    const districtId = selectedDistrict?.value;
    const districtName = selectedDistrict?.label;

    setFormData((prev) => ({
      ...prev,
      district: districtId || null,
      district_name_display: districtName || "",
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "gst_number") {
      const sanitizedValue = value
        .replace(/[^0-9A-Za-z]/g, "")
        .toUpperCase()
        .slice(0, 15);
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
      if (sanitizedValue.length === 0) {
        setGstValidation({ isValid: false, message: "" });
      } else if (sanitizedValue.length !== 15) {
        setGstValidation({
          isValid: false,
          message: `GST Number must be exactly 15 characters (${sanitizedValue.length}/15)`,
        });
      } else {
        const gstPattern = /^[0-9]{2}[0-9A-Z]{10}[0-9]{1}[0-9A-Z]{2}$/;
        if (gstPattern.test(sanitizedValue)) {
          // Check if GST starts with "24" (Morbi state code)
          if (sanitizedValue.startsWith("24")) {
            setGstValidation({
              isValid: true,
              message: "Valid GST Number format",
            });
          } else {
            setGstValidation({
              isValid: false,
              message: "You are outside of Morbi and you can log in to the app as a buyer.",
            });
          }
        } else {
          setGstValidation({
            isValid: false,
            message:
              "Invalid GST Number format. Please check the format: 2 digits (State) + 10 alphanumeric (PAN) + 1 digit (Entity) + 2 alphanumeric (Check sum)",
          });
        }
      }
    } else if (name === "office_mobile") {
      // Only allow digits and limit to 10 characters
      const sanitizedValue = value.replace(/[^0-9]/g, "").slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await accountService.requestOtp({
        mobile_number: "(+91)" + formData.mobile_number,
      });
      if (response.otp_code) {
        setOtpCode(response.otp_code);
        setOtpSent(true);
        toast.success("OTP sent successfully!");
        setShowResendOTP(true);
        startCountdown();
      } else {
        toast.error(
          response.message || "OTP sending failed. Please try again."
        );
      }
    } catch (error) {
      let message = "OTP sending failed. Please try again.";
      if (error.response && error.response.data) {
        message =
          error.response.data.non_field_errors.length > 0
            ? error.response.data.non_field_errors[0]
            : message;
      }
      toast.error(message || "OTP sending failed. Please try again.");
      console.error("Verification error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await accountService.verifyOtp({
        mobile_number: "(+91)" + formData.mobile_number,
        otp_code: otpText,
      });
      if (response.verification_token) {
        toast.success(response.message);
        setFormData({
          ...formData,
          verification_token: response.verification_token,
        });
        setShowResendOTP(false);
      } else {
        toast.error(
          response.message || "OTP verification failed. Please try again."
        );
      }
    } catch (error) {
      let message = "OTP verification failed. Please try again.";
      if (error.response && error.response.data) {
        message =
          error.response.data.non_field_errors.length > 0
            ? error.response.data.non_field_errors[0]
            : message;
      }
      toast.error(message || "OTP verification failed. Please try again.");
      console.error("Verification error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileImageCrop = (cropData) => {
    if (cropData && cropData.fileCropped && cropData.originalFile) {
      const croppedFile = new File(
        [cropData.fileCropped],
        cropData.originalFile.name,
        {
          type: cropData.originalFile.type,
          lastModified: Date.now(),
        }
      );
      const maxSize = MAX_PRODUCT_IMAGE_SIZE * 1024 * 1024; // 10MB
      if (croppedFile.size > maxSize) {
        toast.error(
          "Profile image size should be less than ${MAX_PRODUCT_IMAGE_SIZE}MB"
        );
        return;
      }

      setProfileImage(croppedFile);
      setProfileImagePreview(URL.createObjectURL(croppedFile));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (formData.gst_number.length !== 15) {
        toast.error("GST Number must be exactly 15 characters long");
        setLoading(false);
        return;
      }
      const gstPattern = /^[0-9]{2}[0-9A-Z]{10}[0-9]{1}[0-9A-Z]{2}$/;
      if (!gstPattern.test(formData.gst_number)) {
        toast.error(
          "Invalid GST Number format. Please check the format: 2 digits (State) + 10 alphanumeric (PAN) + 1 digit (Entity) + 2 alphanumeric (Check sum)"
        );
        setLoading(false);
        return;
      }
      // Check if GST starts with "24" (Morbi state code)
      if (!formData.gst_number.startsWith("24")) {
        toast.error("You are outside of Morbi and you can log in to the app as a buyer.");
        setLoading(false);
        return;
      }
      const mobileNumber = `(+91)${formData.mobile_number}`;
      const payload = new FormData();
      payload.append("mobile_number", mobileNumber);
      payload.append("username", formData.username);
      payload.append("pin", formData.pin);
      payload.append("role", "SELLER");
      payload.append("seller_profile.firm_name", formData.firm_name);
      payload.append("seller_profile.gst_number", formData.gst_number);
      payload.append("seller_profile.owner_name", formData.owner_name);
      payload.append("seller_profile.office_mobile", formData.office_mobile);
      payload.append("seller_profile.seller_type", formData.seller_type);
      payload.append("seller_profile.profile_image", profileImage);
      payload.append(
        "seller_profile.address.address_line",
        formData.address_line
      );
      payload.append("seller_profile.address.road", formData.road);
      payload.append("seller_profile.address.city", formData.city);
      payload.append("seller_profile.address.district", formData.district);
      payload.append("seller_profile.address.state", formData.state);
      payload.append("seller_profile.address.pin_code", formData.pin_code);
      formData.category_names.map((category, index) => {
        payload.append(
          `seller_profile.category_names[${index}]`,
          category.value
        );
      });
      payload.append("verification_token", formData.verification_token);
      if (categoryTypes.includes("tiles")) {
        payload.append("seller_profile.tile_type", formData.tile_type);
        payload.append("seller_profile.tile_size", formData.tile_size);
      }
      if (categoryTypes.includes("sanitaryware")) {
        payload.append("seller_profile.seller_place", formData.seller_place);
      }
      const response = await sellerService.signup(payload);
      if (response.mobile_number) {
        toast.success("Signup successful! Please verify your account.");
        navigate("/login");
      } else {
        toast.error(response.message || "Signup failed. Please try again.");
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const errors = error.response.data;
        if (
          error.response.data.non_field_errors &&
          error.response.data.non_field_errors.length
        ) {
          let message =
            error.response.data.non_field_errors.length > 0
              ? error.response.data.non_field_errors[0]
              : message;
          toast.error(message || "Signup failed. Please try again.");
        } else {
          Object.keys(errors).forEach((field) => {
            const messages = errors[field];
            if (Array.isArray(messages)) {
              toast.error(messages[0]);
            }
          });
        }
      }
      console.error("Signup error:", error);
    } finally {
      setLoading(false);
    }
  };

  const isValidData = () => {
    let isTilesValid = true;
    let isSanitarywareValid = true;
    let isOfficeMobileValid = true;

    if (categoryTypes.includes("tiles")) {
      if (!formData.tile_type || !formData.tile_size) {
        isTilesValid = false;
      }
    }

    if (categoryTypes.includes("sanitaryware")) {
      if (!formData.seller_place) {
        isSanitarywareValid = false;
      }
    }

    return (
      profileImage &&
      formData.mobile_number &&
      formData.pin &&
      formData.firm_name &&
      formData.owner_name &&
      formData.gst_number &&
      formData.address_line &&
      formData.road &&
      formData.city &&
      formData.district &&
      formData.state &&
      formData.pin_code &&
      formData.category_names &&
      formData.category_names.length > 0 &&
      formData.verification_token &&
      isTilesValid &&
      isSanitarywareValid &&
      isOfficeMobileValid
    );
  };

  useEffect(() => {
    if (formData.category_names.length > 0) {
      setCategoryTypes(
        formData.category_names.map((category) => {
          return category.value;
        })
      );
    } else {
      setCategoryTypes([]);
    }
  }, [formData.category_names]);

  useEffect(() => {
    fetchCategories();
    fetchTypes();
    fetchSizes();
    fetchStates();
  }, []);

  useEffect(() => {
    return () => {
      if (profileImagePreview) {
        URL.revokeObjectURL(profileImagePreview);
      }
    };
  }, [profileImagePreview]);

  useEffect(() => {
    if (remaining <= 0) return;
    const timer = setInterval(() => {
      setRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [remaining]);

  return (
    <div className="main-wrapper">
      <div className="text-center mb-4">
        <div className="logo mb-3">
          <img
            src={"/assets/img/logo.png"}
            alt="Morbi Bazaar Logo"
            className="logo-image"
          />
        </div>
        <div
          className="text-center mb-4"
          style={{ color: "#006666", fontSize: "16px" }}
        >
          Join our network of ceramic sellers across India.
        </div>
      </div>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-12">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-6 mb-4">
                  <div
                    style={{
                      backgroundColor: "white",
                      borderRadius: "16px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      padding: "2rem",
                      height: "100%",
                    }}
                  >
                    <div
                      className="fw-semibold mb-3 d-flex align-items-center"
                      style={{
                        color: "#006666",
                        fontSize: "18px",
                      }}
                    >
                      <i className="fas fa-phone me-2"></i>
                      Authentication & Profile
                    </div>
                    {/* Mobile Number */}
                    <div className="row">
                      <div className="form-group position-relative col-md-8">
                        <label>
                          Mobile Number{" "}
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>
                        <div className="d-flex align-items-center">
                          <input
                            type="text"
                            className="form-control mobile-number-input input-form-input"
                            name="mobile_number"
                            value={formData.mobile_number}
                            onChange={handleInputChange}
                            disabled={formData.verification_token}
                            placeholder="Enter mobile number"
                            autoComplete="off"
                            maxLength="10"
                            pattern="[0-9]{10}"
                            title="Please enter a valid 10-digit mobile number"
                            required
                          />
                          <span className="span-signup-91">+91</span>
                        </div>
                      </div>
                      <div className="form-group col-md-4">
                        <label
                          style={{
                            color: "#333",
                            fontSize: "14px",
                            fontWeight: "500",
                            marginBottom: "8px",
                            display: "block",
                          }}
                        >
                          &nbsp;
                        </label>
                        <button
                          type="button"
                          onClick={sendOtp}
                          className="btn btn-block login-button"
                          disabled={formData.mobile_number.length !== 10 || otpSent}
                          style={{
                            transition: "all 0.3s ease",
                            backgroundColor:
                              formData.mobile_number.length !== 10 || otpSent
                                ? "#ccc"
                                : "",
                            cursor:
                              formData.mobile_number.length !== 10 || otpSent
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              formData.mobile_number.length !== 10 || otpSent ? 0.6 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (formData.mobile_number.length === 10 && !otpSent) {
                              e.target.style.backgroundColor =
                                "#31ebe1ff !important";
                              e.target.style.transform = "translateY(-3px)";
                              e.target.style.boxShadow =
                                "0 5px 9px rgba(32, 178, 170, 0.3)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (formData.mobile_number.length === 10 && !otpSent) {
                              e.target.style.backgroundColor = "";
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow = "";
                            }
                          }}
                        >
                          Verify
                        </button>
                      </div>
                    </div>
                    {!formData.verification_token && otpCode && (
                      <div className="row">
                        <div className="form-group col-md-8">
                          <label>
                            OTP Code{" "}
                            <div
                              className="d-inline"
                              style={{ color: "#e74c3c" }}
                            >
                              *
                            </div>
                          </label>
                          <input
                            type="text"
                            className="form-control input-form-input"
                            name="otp_code"
                            value={otpText}
                            onChange={(e) => setOtpText(e.target.value)}
                            placeholder="Enter OTP code"
                            maxLength="6"
                            pattern="[0-9]{6}"
                            title="Please enter a valid 6-digit OTP code"
                            required
                          />
                        </div>
                        <div className="form-group col-md-4">
                          <label
                            style={{
                              color: "#333",
                              fontSize: "14px",
                              fontWeight: "500",
                              marginBottom: "8px",
                              display: "block",
                            }}
                          >
                            &nbsp;
                          </label>
                          <button
                            type="button"
                            onClick={handleVerify}
                            className="btn login-button w-100"
                            style={{
                              backgroundColor: "#006666",
                              color: "white",
                              border: "none",
                              borderRadius: "8px",
                              fontSize: "14px",
                              fontWeight: "500",
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = "#077771ff";
                              e.target.style.transform = "translateY(-2px)";
                              e.target.style.boxShadow =
                                "0 4px 8px rgba(10, 172, 164, 0.3)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = "#068181ff";
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow = "";
                            }}
                          >
                            Verify
                          </button>
                        </div>
                      </div>
                    )}
                    {formData.verification_token && (
                      <div className="form-group">
                        <div
                          className="text-success fw-medium"
                          style={{
                            fontSize: "14px",
                          }}
                        >
                          <i className="fas fa-check-circle me-1"></i>
                          Mobile number verified
                        </div>
                      </div>
                    )}
                    {showResendOTP && (
                      <div className="form-group">
                        <div
                          style={{
                            color: "#e74c3c",
                            fontSize: "14px",
                            marginBottom: "8px",
                          }}
                        >
                          Resend OTP in {formatTime(remaining)}
                        </div>
                        <button
                          type="button"
                          disabled={remaining > 0}
                          onClick={sendOtp}
                          style={{
                            padding: "8px 16px",
                            backgroundColor: remaining > 0 ? "#ccc" : "#006666",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "12px",
                            cursor: remaining > 0 ? "not-allowed" : "pointer",
                            transition: "all 0.3s ease",
                          }}
                        >
                          Resend OTP
                        </button>
                      </div>
                    )}
                    <div className="row">
                      <div className="form-group col-md-6">
                        <label>
                          4-digit PIN{" "}
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>
                        <div className="position-relative">
                          <input
                            type="password"
                            className="form-control input-form-input"
                            name="pin"
                            value={formData.pin}
                            onChange={handleInputChange}
                            placeholder="4-digit PIN"
                            maxLength="4"
                            size={4}
                            pattern="[0-9]{4}"
                            title="PIN should be 4 digits"
                            required
                          />
                        </div>
                      </div>
                      <div className="form-group col-md-6">
                        <label>
                          Confirm PIN{" "}
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>
                        <div className="position-relative">
                          <input
                            type="password"
                            className="form-control input-form-input"
                            placeholder="Confirm 4-digit PIN"
                            maxLength="4"
                            size={4}
                            pattern="[0-9]{4}"
                            title="PIN should be 4 digits"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>
                        Profile Image
                        <div className="d-inline" style={{ color: "#e74c3c" }}>
                          *
                        </div>
                      </label>

                      <Crop
                        cropShape="rect"
                        onChangeCropper={handleProfileImageCrop}
                        maxSizeMib={10}
                        height={200}
                        width={200}
                        aspectRatio={1}
                        imagePreview={profileImagePreview}
                        isButtonHidden={true}
                        guid="profile-image-crop"
                        buttonBackGroundColor="#f8f9fa"
                      />
                      {profileImage && (
                        <div className="mt-2">
                          <small
                            style={{
                              color: "#27ae60",
                              fontSize: "12px",
                              display: "block",
                              marginBottom: "4px",
                            }}
                          >
                            <i className="fas fa-check-circle me-1"></i>
                            Profile image ready: {profileImage.name}
                          </small>
                          <button
                            type="button"
                            style={{
                              padding: "4px 8px",
                              backgroundColor: "transparent",
                              color: "#e74c3c",
                              border: "1px solid #e74c3c",
                              borderRadius: "4px",
                              fontSize: "12px",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setProfileImage(null);
                              if (profileImagePreview) {
                                URL.revokeObjectURL(profileImagePreview);
                                setProfileImagePreview(null);
                              }
                            }}
                          >
                            <i className="fas fa-times me-1"></i> Remove Image
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Card - Business Details */}
                <div className="col-lg-6 mb-4">
                  <div
                    style={{
                      backgroundColor: "white",
                      borderRadius: "16px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      padding: "2rem",
                      height: "100%",
                    }}
                  >
                    <div
                      className="fw-semibold mb-3 d-flex align-items-center"
                      style={{
                        color: "#006666",
                        fontSize: "18px",
                      }}
                    >
                      <i className="fas fa-building me-2"></i>
                      Business Details
                    </div>
                    {/* Firm Name */}
                    <div className="row">
                      <div className="form-group col-md-6">
                        <label>
                          Firm Name{" "}
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>
                        <input
                          type="text"
                          className="form-control input-form-input"
                          name="firm_name"
                          value={formData.firm_name}
                          onChange={handleInputChange}
                          placeholder="Enter firm name"
                          required
                        />
                      </div>
                      <div className="form-group col-md-6">
                        <label>
                          GST Number
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>

                        <input
                          type="text"
                          className="form-control input-form-input"
                          name="gst_number"
                          value={formData.gst_number}
                          onChange={handleInputChange}
                          placeholder="GST Number"
                          maxLength="15"
                          pattern="[0-9A-Z]{15}"
                          title="GST Number must be exactly 15 characters"
                          required
                        />
                        {gstValidation.message && (
                          <div
                            style={{
                              fontSize: "12px",
                              color: gstValidation.isValid
                                ? "#27ae60"
                                : "#e74c3c",
                              marginTop: "4px",
                            }}
                          >
                            {gstValidation.message}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Owner Name */}
                    <div className="row">
                      <div className="form-group col-md-6">
                        <label>
                          Owner Name{" "}
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>
                        <input
                          type="text"
                          className="form-control input-form-input"
                          name="owner_name"
                          value={formData.owner_name}
                          onChange={handleInputChange}
                          placeholder="Enter owner name"
                          required
                        />
                      </div>
                      <div className="form-group col-md-6 position-relative">
                        <label>Office Mobile</label>

                        <div className="d-flex align-items-center">
                          <input
                            type="text"
                            className="form-control mobile-number-input input-form-input"
                            name="office_mobile"
                            value={formData.office_mobile}
                            onChange={handleInputChange}
                            placeholder="Enter Office mobile number (Optional)"
                            maxLength="10"
                            pattern="[0-9]{10}"
                            title="Please enter a valid 10-digit mobile number"
                          />
                          <span className="span-signup2-91">+91</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div
                        className="fw-semibold mb-2 d-flex align-items-center"
                        style={{
                          color: "#006666",
                          fontSize: "16px",
                        }}
                      >
                        <i className="fas fa-map-marker-alt me-2"></i>
                        Office Address
                      </div>
                      <div className="form-group">
                        <label>
                          Address Line{" "}
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>
                        <textarea
                          className="form-control input-form-input"
                          name="address_line"
                          value={formData.address_line}
                          onChange={handleInputChange}
                          placeholder="Enter complete address"
                          required
                        />
                      </div>

                      {/* Road */}
                      <div className="row">
                        <div className="form-group col-md-6">
                          <label>
                            Road{" "}
                            <div
                              className="d-inline"
                              style={{ color: "#e74c3c" }}
                            >
                              *
                            </div>
                          </label>
                          <input
                            type="text"
                            className="form-control input-form-input"
                            name="road"
                            value={formData.road}
                            onChange={handleInputChange}
                            placeholder="Enter road name"
                            required
                          />
                        </div>
                        <div className="form-group col-md-6">
                          <label>
                            City{" "}
                            <div
                              className="d-inline"
                              style={{ color: "#e74c3c" }}
                            >
                              *
                            </div>
                          </label>
                          <input
                            type="text"
                            className="form-control input-form-input"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            placeholder="Enter city"
                            required
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="form-group col-md-6">
                          <label>
                            PIN Code{" "}
                            <div
                              className="d-inline"
                              style={{ color: "#e74c3c" }}
                            >
                              *
                            </div>
                          </label>
                          <input
                            type="text"
                            className="form-control input-form-input"
                            name="pin_code"
                            value={formData.pin_code}
                            onChange={handleInputChange}
                            placeholder="123456"
                            maxLength="6"
                            pattern="[0-9]{6}"
                            required
                          />
                        </div>
                        <div className="form-group col-md-6">
                          <label>
                            State{" "}
                            <div
                              className="d-inline"
                              style={{ color: "#e74c3c" }}
                            >
                              *
                            </div>
                          </label>
                          <Select
                            styles={{
                              control: (base) => ({
                                ...base,
                                border: "2px solid #e0f2f1",
                                borderRadius: "8px",
                                backgroundColor: "#DEEDED",
                                minHeight: "48px",
                                "&:hover": {
                                  borderColor: "#DEEDED",
                                },
                              }),
                              option: (styles, { isFocused, isSelected }) => ({
                                ...styles,
                                backgroundColor: isFocused
                                  ? "#DEEDED"
                                  : isSelected
                                  ? "#DEEDED"
                                  : "white",
                                color: isFocused
                                  ? "#333"
                                  : isSelected
                                  ? "white"
                                  : "#333",
                                padding: "12px 16px",
                              }),
                              singleValue: (styles) => ({
                                ...styles,
                                color: "#333",
                              }),
                              placeholder: (styles) => ({
                                ...styles,
                                color: "#999",
                              }),
                            }}
                            options={states}
                            value={states.find(
                              (state) => state.value === formData.state
                            )}
                            onChange={handleStateChange}
                            placeholder="Select state"
                            required
                          />
                        </div>
                      </div>
                      <div className="row">
                      <div className="form-group col-md-6">
                        <label>
                          District{" "}
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>
                        <Select
                          styles={{
                            control: (base) => ({
                              ...base,
                              border: "2px solid #e0f2f1",
                              borderRadius: "8px",
                              backgroundColor: "#DEEDED",
                              minHeight: "48px",
                              "&:hover": {
                                borderColor: "#DEEDED",
                              },
                            }),
                            option: (styles, { isFocused, isSelected }) => ({
                              ...styles,
                              backgroundColor: isFocused
                                ? "#DEEDED"
                                : isSelected
                                ? "#DEEDED"
                                : "white",
                              color: isFocused
                                ? "#333"
                                : isSelected
                                ? "white"
                                : "#333",
                              padding: "12px 16px",
                            }),
                            singleValue: (styles) => ({
                              ...styles,
                              color: "#333",
                            }),
                            placeholder: (styles) => ({
                              ...styles,
                              color: "#999",
                            }),
                          }}
                          options={districts}
                          value={
                            districts.find(
                              (district) => district.value === formData.district
                            ) || null
                          }
                          onChange={handleDistrictChange}
                          placeholder="Select district"
                          isDisabled={!formData.state}
                          required
                        />
                      </div>
                      <div className="form-group b-4 col-md-6">
                        <label>
                          Product Categories
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>
                        <Select
                          isMulti
                          styles={{
                            control: (base) => ({
                              ...base,
                              border: "2px solid #e0f2f1",
                              borderRadius: "8px",
                              backgroundColor: "#DEEDED",
                              minHeight: "48px",
                              "&:hover": {
                                borderColor: "#DEEDED",
                              },
                            }),
                            option: (styles, { isFocused, isSelected }) => ({
                              ...styles,
                              backgroundColor: isFocused
                                ? "#DEEDED"
                                : isSelected
                                ? "#DEEDED"
                                : "white",
                              color: isFocused
                                ? "#333"
                                : isSelected
                                ? "white"
                                : "#333",
                              padding: "12px 16px",
                            }),
                            multiValue: (styles) => ({
                              ...styles,
                              backgroundColor: "#DEEDED",
                              borderRadius: "6px",
                            }),
                            multiValueLabel: (styles) => ({
                              ...styles,
                              color: "#333",
                            }),
                            multiValueRemove: (styles) => ({
                              ...styles,
                              color: "#666",
                              "&:hover": {
                                backgroundColor: "#c0c0c0",
                                color: "#333",
                              },
                            }),
                            placeholder: (styles) => ({
                              ...styles,
                              color: "#999",
                            }),
                          }}
                          options={categories}
                          value={formData.category_names}
                          onChange={(selectedOptions) => {
                            setFormData({
                              ...formData,
                              category_names: selectedOptions || [],
                            });
                          }}
                          placeholder="Select categories"
                          required
                        />
                      </div>
                      </div>
                    </div>

                    {/* Product Categories */}

                    {/* Seller Type */}
                    <div className="form-group">
                      <label>
                        Seller Type{" "}
                        <div className="d-inline" style={{ color: "#e74c3c" }}>
                          *
                        </div>
                      </label>
                      <Select
                        styles={{
                          control: (base) => ({
                            ...base,
                            border: "2px solid #e0f2f1",
                            borderRadius: "8px",
                            backgroundColor: "#DEEDED",
                            minHeight: "48px",
                            "&:hover": {
                              borderColor: "#DEEDED",
                            },
                          }),
                          option: (styles, { isFocused, isSelected }) => ({
                            ...styles,
                            backgroundColor: isFocused
                              ? "#DEEDED"
                              : isSelected
                              ? "#DEEDED"
                              : "white",
                            color: isFocused
                              ? "#333"
                              : isSelected
                              ? "white"
                              : "#333",
                            padding: "12px 16px",
                          }),
                          singleValue: (styles) => ({
                            ...styles,
                            color: "#333",
                          }),
                          placeholder: (styles) => ({
                            ...styles,
                            color: "#999",
                          }),
                        }}
                        options={sellerTypes}
                        value={sellerTypes.find(
                          (type) => type.value === formData.seller_type
                        )}
                        onChange={(selectedOptions) => {
                          setFormData({
                            ...formData,
                            seller_type: selectedOptions.value || "",
                          });
                        }}
                        placeholder="Select seller type"
                        required
                      />
                    </div>
                    {categoryTypes.includes("sanitaryware") && (
                      <div className="form-group">
                        <label
                          style={{
                            color: "#333",
                            fontSize: "14px",
                            fontWeight: "500",
                            marginBottom: "8px",
                            display: "block",
                          }}
                        >
                          Seller Place{" "}
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>
                        <Select
                          styles={{
                            control: (base) => ({
                              ...base,
                              border: "2px solid #e0f2f1",
                              borderRadius: "8px",
                              backgroundColor: "#DEEDED",
                              minHeight: "48px",
                              "&:hover": {
                                borderColor: "#DEEDED",
                              },
                            }),
                            option: (styles, { isFocused, isSelected }) => ({
                              ...styles,
                              backgroundColor: isFocused
                                ? "#DEEDED"
                                : isSelected
                                ? "#DEEDED"
                                : "white",
                              color: isFocused
                                ? "#333"
                                : isSelected
                                ? "white"
                                : "#333",
                              padding: "12px 16px",
                            }),
                            singleValue: (styles) => ({
                              ...styles,
                              color: "#333",
                            }),
                            placeholder: (styles) => ({
                              ...styles,
                              color: "#999",
                            }),
                          }}
                          options={SELLER_PLACE_CHOICES}
                          value={SELLER_PLACE_CHOICES.find(
                            (opt) => opt.value === formData.seller_place
                          )}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              seller_place: selected?.value || "",
                            }))
                          }
                          placeholder="Select seller place"
                          required
                        />
                      </div>
                    )}
                    {categoryTypes.includes("tiles") && (
                      <div className="form-group">
                        <label
                          style={{
                            color: "#333",
                            fontSize: "14px",
                            fontWeight: "500",
                            marginBottom: "8px",
                            display: "block",
                          }}
                        >
                          Tile Type{" "}
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>
                        <Select
                          styles={{
                            control: (base) => ({
                              ...base,
                              border: "2px solid #e0f2f1",
                              borderRadius: "8px",
                              backgroundColor: "#DEEDED",
                              minHeight: "48px",
                              "&:hover": {
                                borderColor: "#DEEDED",
                              },
                            }),
                            option: (styles, { isFocused, isSelected }) => ({
                              ...styles,
                              backgroundColor: isFocused
                                ? "#DEEDED"
                                : isSelected
                                ? "#DEEDED"
                                : "white",
                              color: isFocused
                                ? "#333"
                                : isSelected
                                ? "white"
                                : "#333",
                              padding: "12px 16px",
                            }),
                            singleValue: (styles) => ({
                              ...styles,
                              color: "#333",
                            }),
                            placeholder: (styles) => ({
                              ...styles,
                              color: "#999",
                            }),
                          }}
                          options={types}
                          value={types.find(
                            (opt) => opt.value === formData.tile_type
                          )}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              tile_type: selected?.value || "",
                            }))
                          }
                          placeholder="Select tile type"
                          required
                        />
                      </div>
                    )}
                    {categoryTypes.includes("tiles") && (
                      <div className="form-group">
                        <label
                          style={{
                            color: "#333",
                            fontSize: "14px",
                            fontWeight: "500",
                            marginBottom: "8px",
                            display: "block",
                          }}
                        >
                          Tile Size{" "}
                          <div
                            className="d-inline"
                            style={{ color: "#e74c3c" }}
                          >
                            *
                          </div>
                        </label>
                        <Select
                          styles={{
                            control: (base) => ({
                              ...base,
                              border: "2px solid #e0f2f1",
                              borderRadius: "8px",
                              backgroundColor: "#DEEDED",
                              minHeight: "48px",
                              "&:hover": {
                                borderColor: "#DEEDED",
                              },
                            }),
                            option: (styles, { isFocused, isSelected }) => ({
                              ...styles,
                              backgroundColor: isFocused
                                ? "#DEEDED"
                                : isSelected
                                ? "#DEEDED"
                                : "white",
                              color: isFocused
                                ? "#333"
                                : isSelected
                                ? "white"
                                : "#333",
                              padding: "12px 16px",
                            }),
                            singleValue: (styles) => ({
                              ...styles,
                              color: "#333",
                            }),
                            placeholder: (styles) => ({
                              ...styles,
                              color: "#999",
                            }),
                          }}
                          options={sizes}
                          value={sizes.find(
                            (opt) => opt.value === formData.tile_size
                          )}
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              tile_size: selected?.value || "",
                            }))
                          }
                          placeholder="Select tile size"
                          required
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-center mt-4">
                <button
                  type="submit"
                  style={{
                    padding: "16px 48px",
                    backgroundColor:
                      loading || !isValidData() ? "#b3d9d9" : "#006666",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "18px",
                    fontWeight: "600",
                    cursor:
                      loading || !isValidData() ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    boxShadow:
                      loading || !isValidData()
                        ? "0 2px 6px rgba(179, 217, 217, 0.3)"
                        : "0 4px 12px rgba(32, 178, 170, 0.3)",
                    minWidth: "200px",
                    opacity: loading || !isValidData() ? 0.7 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && isValidData()) {
                      e.target.style.backgroundColor = "#008080";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 6px 16px rgba(32, 178, 170, 0.4)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading && isValidData()) {
                      e.target.style.backgroundColor = "#006666";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 4px 12px rgba(32, 178, 170, 0.3)";
                    }
                  }}
                  disabled={loading || !isValidData()}
                >
                  {loading ? "Creating Account..." : "Register"}
                </button>

                <div className="mt-3 mb-3">
                  <div className="text-muted">
                    Already have an account?{" "}
                    <a
                      href="/login"
                      className="text-decoration-none fw-medium"
                      style={{ color: "#006666" }}
                    >
                      Login here
                    </a>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default AccountSignUp;
